import Axios from 'axios'
import React, { FormEvent, useEffect, useState } from 'react'
import { FunctionComponent } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Material } from './materials/material'
import AsyncSelect from 'react-select/async'

export const EditContractor: FunctionComponent = () => {
  const [disabled, setDisabled] = useState(false)
  const [name, setName] = useState('')
  const [inn, setInn] = useState('')
  const [type, setType] = useState(0)
  const [contractorObjects, setContractorObjects] = useState<
    {
      name: string
      address: string
      addressId: string
      id: string
      new: boolean
    }[]
  >([])
  const [materials, setMaterials] = useState<
    {
      id?: string
      material: { id: string; name: string; archived: boolean }
      cost: number
      volumeType: number
      contractorObjectNames: {
        label: string
        value: string
        addressId: string
      }[]
      actualDateFrom: string
    }[]
  >([])
  const [typeThreeRentPerHour, setTypeThreeRentPerHourt] = useState(0)
  const [typeFourRentPerHour, setTypeFourRentPerHourt] = useState(0)
  const [typeFiveRentPerHour, setTypeFiveRentPerHourt] = useState(0)
  const [payerId, setPayerId] = useState<string>()
  const [payerName, setPayerName] = useState<string>()
  const [payerInn, setPayerInn] = useState<string>()
  const [recalculate, setRecalculate] = useState(false)

  const { contractorId } = useParams<{ contractorId: string }>()

  useEffect(() => {
    const loadContractor = async () => {
      setDisabled(true)

      try {
        const contractor = await Axios.get<{
          name: string
          inn: string
          type: number
          contractorObjects: {
            id: string
            name: string
            address: string
            addressId: string
            new: boolean
          }[]
          materials: {
            id: string
            cost: number
            volumeType: number
            material: { id: string; name: string; archived: boolean }
            contractorObjectsIds: string[]
            actualDateFrom: string
          }[]
          typeThreeRentPerHour: number
          typeFourRentPerHour: number
          typeFiveRentPerHour: number
          payerId?: string
          payerName?: string
          payerInn?: string
        }>(
          `${process.env.REACT_APP_API_URL}/api/v1/contractors/${contractorId}`
        )

        setName(contractor.data.name)
        setInn(contractor.data.inn)
        setType(contractor.data.type)

        setContractorObjects(contractor.data.contractorObjects)
        setMaterials(
          contractor.data.materials.map((_) => {
            return {
              id: _.id,
              material: _.material,
              cost: _.cost,
              volumeType: _.volumeType,
              contractorObjectNames: _.contractorObjectsIds.map((_) => {
                const contractorObject = contractor.data.contractorObjects.filter(
                  (contractorObject) => contractorObject.id === _
                )[0]

                return {
                  label: `${contractorObject.name} - ${contractorObject.address}`,
                  value: contractorObject.id,
                  addressId: contractorObject.addressId,
                }
              }),
              actualDateFrom: _.actualDateFrom,
            }
          })
        )

        setTypeThreeRentPerHourt(contractor.data.typeThreeRentPerHour)
        setTypeFourRentPerHourt(contractor.data.typeFourRentPerHour)
        setTypeFiveRentPerHourt(contractor.data.typeFiveRentPerHour)

        if (contractor.data.payerId) {
          setPayerId(contractor.data.payerId)
          setPayerName(contractor.data.payerName)
          setPayerInn(contractor.data.payerInn)
        }
      } finally {
        setDisabled(false)
      }
    }

    loadContractor()
  }, [contractorId])

  const editContractor = async (_: FormEvent) => {
    _.preventDefault()

    setDisabled(true)

    try {
      await Axios.put(
        `${process.env.REACT_APP_API_URL}/api/v1/contractors/${contractorId}`,
        {
          name,
          inn,
          type,
          objects: contractorObjects,
          materials: materials.map((_) => {
            return {
              id: _.id,
              materialId: _.material.id,
              cost: _.cost,
              volumeType: _.volumeType,
              contractorObjectsIds: _.contractorObjectNames.map((_) => _.value),
              actualDateFrom: _.actualDateFrom === '' ? null : _.actualDateFrom,
            }
          }),
          typeThreeRentPerHour,
          typeFourRentPerHour,
          typeFiveRentPerHour,
          payerId,
        }
      )

      toast.success('Контрагент обновлён.')

      setRecalculate(false)
    } finally {
      setDisabled(false)
    }
  }

  return (
    <div className="row">
      <div className="col">
        <form onSubmit={editContractor}>
          <fieldset disabled={disabled}>
            <div className="form-group">
              <label>Грузополучатель</label>
              <input
                className="form-control"
                required
                type="text"
                value={name}
                onChange={(_) => setName(_.target.value)}
              />
            </div>
            <div className="form-group">
              <label>ИНН</label>
              <input
                className="form-control"
                type="text"
                value={inn}
                onChange={(_) => setInn(_.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Плательщик</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isClearable={true}
                value={{
                  value: payerId,
                  label: payerName ? `${payerName}-${payerInn}` : '',
                  name: payerName,
                  inn: payerInn,
                }}
                loadOptions={(inputValue, callback) => {
                  const loadPayers = async (
                    inputValue: string,
                    callback: any
                  ) => {
                    const cars = await Axios.get<{
                      items: { id: string; name: string; inn: string }[]
                    }>(
                      `${process.env.REACT_APP_API_URL}/api/v1/payers?query=${inputValue}&page=1`
                    )

                    callback(
                      cars.data.items.map((_) => {
                        return {
                          value: _.id,
                          label: `${_.name}-${_.inn}`,
                          name: _.name,
                          inn: _.inn,
                        }
                      })
                    )
                  }

                  loadPayers(inputValue, callback)
                }}
                onChange={(_: any) => {
                  if (_) {
                    setPayerId(_.value)
                    setPayerName(_.name)
                    setPayerInn(_.inn)
                    return
                  }

                  setPayerId(undefined)
                  setPayerName(undefined)
                  setPayerInn(undefined)
                }}
              />
            </div>
            <div className="form-group">
              <label>Тип</label>
              <select
                className="form-control"
                value={type}
                onChange={(_) => setType(parseInt(_.target.value))}
              >
                <option value={0}>Объект</option>
                <option value={1}>Карьер</option>
                <option value={2}>Полигон</option>
                <option value={3}>База</option>
              </select>
            </div>

            <div className="form-group">
              <label>Адреса</label>
            </div>
            {contractorObjects.map((_, index) => (
              <div className="form-row">
                <div className="form-group col-lg-11">
                  <AsyncSelect
                    key={`contractor_object_${new Date().toString()}`}
                    cacheOptions
                    defaultOptions
                    value={{
                      value: _.id,
                      label: `${_.name} - ${_.address}`,
                    }}
                    loadOptions={(inputValue, callback) => {
                      const loadAddresses = async (
                        inputValue: string,
                        callback: any
                      ) => {
                        const addresses = await Axios.get<{
                          items: { id: string; name: string; value: string }[]
                        }>(
                          `${process.env.REACT_APP_API_URL}/api/v1/addresses?query=${inputValue}&page=1`
                        )

                        callback(
                          addresses.data.items.map((_) => {
                            return {
                              value: _.id,
                              label: `${_.name} - ${_.value}`,
                              name: _.name,
                              address: _.value,
                            }
                          })
                        )
                      }

                      loadAddresses(inputValue, callback)
                    }}
                    onChange={(_: any) => {
                      for (let i = 0; i < materials.length; i++) {
                        for (
                          let j = 0;
                          j < materials[i].contractorObjectNames.length;
                          j++
                        ) {
                          if (
                            materials[i].contractorObjectNames[j].value ===
                            contractorObjects[index].id
                          ) {
                            materials[i].contractorObjectNames[j].addressId =
                              _.value

                            materials[i].contractorObjectNames[j].label =
                              _.label
                          }
                        }
                      }

                      if (!contractorObjects[index].id) {
                        contractorObjects[index].id = _.value
                      }

                      contractorObjects[index].name = _.name
                      contractorObjects[index].address = _.address
                      contractorObjects[index].addressId = _.value

                      setContractorObjects([...contractorObjects])
                      setMaterials([...materials])
                    }}
                  />
                </div>
                <div className="form-group col-lg-1">
                  <button
                    key={`contractor_object_delete_button_${new Date().toString()}`}
                    type="button"
                    onClick={() => {
                      setContractorObjects(
                        contractorObjects.filter((_, i) => i !== index)
                      )
                    }}
                    className="btn btn-danger align-bottom"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}
            <div className="form-group">
              <button
                type="button"
                onClick={() =>
                  setContractorObjects([
                    ...contractorObjects,
                    { name: '', address: '', id: '', addressId: '', new: true },
                  ])
                }
                className="btn btn-link"
              >
                Добавить адрес
              </button>
            </div>

            <div className="form-group">
              <label>Цены на материалы</label>
            </div>
            {materials.map((_, index) => (
              <div className="form-row">
                <div className="form-group col-lg-11">
                  <Material
                    key={`material_${new Date().toString()}`}
                    material={_.material}
                    cost={_.cost}
                    volumeType={_.volumeType}
                    contractorObjectNames={_.contractorObjectNames}
                    setMaterial={(_) => {
                      materials[index].material = _
                      setMaterials([...materials])
                    }}
                    setCost={(_) => {
                      materials[index].cost = _
                      setMaterials([...materials])
                    }}
                    setContractorObjectNames={(_) => {
                      materials[index].contractorObjectNames = _
                      setMaterials([...materials])
                    }}
                    setVolumeType={(_) => {
                      materials[index].volumeType = _
                      setMaterials([...materials])
                    }}
                    getContractorObjectNames={(_) => {
                      if (!_) {
                        return contractorObjects
                          .filter((contractorObject) => contractorObject.name)
                          .map((contractorObject) => {
                            return {
                              label: `${contractorObject.name} - ${contractorObject.address}`,
                              value: `${contractorObject.id}`,
                            }
                          })
                      }

                      return contractorObjects
                        .filter(
                          (contractorObject) =>
                            contractorObject.name
                              .toLowerCase()
                              .indexOf(_.toLowerCase()) !== -1
                        )
                        .map((contractorObject) => {
                          return {
                            label: contractorObject.name,
                            value: contractorObject.id,
                          }
                        })
                    }}
                    actualDateFrom={_.actualDateFrom}
                    setActualDateFrom={(_) => {
                      materials[index].actualDateFrom = _
                      setMaterials([...materials])
                    }}
                    setRecalculate={(_) => setRecalculate(_)}
                  />
                </div>
                <div className="form-group col-lg-1">
                  <button
                    key={`material_delete_button_${new Date().toString()}`}
                    type="button"
                    onClick={() => {
                      setMaterials(materials.filter((_, i) => i !== index))
                    }}
                    className="btn btn-danger align-bottom"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}
            <div className="form-group">
              <button
                type="button"
                onClick={() =>
                  setMaterials([
                    ...materials,
                    {
                      id: undefined,
                      material: { id: '', name: '', archived: false },
                      cost: 0,
                      contractorObjectNames: [],
                      volumeType: 0,
                      actualDateFrom: '',
                    },
                  ])
                }
                className="btn btn-link"
              >
                Добавить материал
              </button>
            </div>

            <div className="form-group">
              <label>Цены за аренду за час</label>
            </div>
            <div className="form-group">
              <label>3 осная</label>
              <input
                type="number"
                min={0}
                step={0.01}
                required
                className="form-control"
                value={typeThreeRentPerHour}
                onChange={(_) =>
                  setTypeThreeRentPerHourt(parseFloat(_.target.value))
                }
              />
            </div>
            <div className="form-group">
              <label>4 осная</label>
              <input
                type="number"
                min={0}
                step={0.01}
                required
                className="form-control"
                value={typeFourRentPerHour}
                onChange={(_) =>
                  setTypeFourRentPerHourt(parseFloat(_.target.value))
                }
              />
            </div>
            <div className="form-group">
              <label>5 осная</label>
              <input
                type="number"
                min={0}
                step={0.01}
                required
                className="form-control"
                value={typeFiveRentPerHour}
                onChange={(_) =>
                  setTypeFiveRentPerHourt(parseFloat(_.target.value))
                }
              />
            </div>
            <div className="form-group">
              {recalculate ? (
                <span className="text-danger">
                  Внимание! Цена будет пересчитана во всех уже совершенных
                  заказах этого контрагента.
                </span>
              ) : null}
            </div>
            <button type="submit" className="btn btn-success">
              <i className="fas fa-save"></i> Сохранить
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  )
}
